<template>
  <div class="sena-ranking">
    <va-card :title="cardTitle">
      <template slot="actions">
        <va-button
          color="gray"
          icon="fa fa-refresh"
          @click="refetchAll"
        />
      </template>

      <!-- A manchete. Cai a cada resultado lançado. -->
      <div class="sena-summary mb-3" v-if="progress">
        <span class="sena-summary__item">
          <b>{{ progress.tickets_alive }}</b>
          {{ $t('sena.labels.of') }} {{ progress.tickets_total }}
          {{ $t('sena.labels.survivors') }}
        </span>
        <!-- Quebra por faixa: com 1 erro o bilhete sai da disputa dos 6 e
             continua na dos 5. Sem isso o número de cima parece inflado. -->
        <span class="sena-summary__item">
          <b>{{ progress.tickets_alive_6 }}</b> {{ $t('sena.labels.prizeTier6') }}
          <span class="sena-muted">·</span>
          <b>{{ progress.tickets_alive_5 }}</b> {{ $t('sena.labels.prizeTier5') }}
        </span>
        <span class="sena-summary__item">
          <b>{{ progress.games_resolved }}/{{ progress.games_total }}</b>
          {{ $t('sena.labels.gamesResolved') }}
        </span>
        <span class="sena-summary__item" v-if="progress.games_blocked > 0">
          <va-badge color="danger">{{ $t('sena.labels.willVoid') }}</va-badge>
        </span>
      </div>

      <va-tabs v-model="tab" grow>
        <va-tab>{{ $t('sena.tabs.survivors') }}</va-tab>
        <va-tab>{{ $t('sena.tabs.standings') }}</va-tab>
      </va-tabs>

      <!-- ABA 1 — sobreviventes.
           Vazia até o resultado do 1º jogo: sena_ranking exige hits > 0.
           Isso é o comportamento correto, não falha de carregamento — daí o
           estado vazio próprio em vez do "Sem Dados Cadastrados" genérico. -->
      <div v-show="tab === 0" class="mt-3">
        <div v-if="!lockoutPassed" class="sena-empty">
          {{ $t('sena.empty.beforeLockout') }}
        </div>
        <div v-else-if="progress && progress.games_resolved === 0" class="sena-empty">
          {{ $t('sena.empty.beforeFirstResult') }}
        </div>
        <div v-else-if="ranking.length === 0" class="sena-empty">
          {{ $t('sena.empty.noSurvivors') }}
        </div>
        <va-data-table
          v-else
          :fields="rankingFields"
          :data="ranking"
          :per-page="perPage"
          no-data-label=""
        />
      </div>

      <!-- ABA 2 — todos, com acertos parciais. Eliminados esmaecidos. -->
      <div v-show="tab === 1" class="mt-3">
        <div v-if="!lockoutPassed" class="sena-empty">
          {{ $t('sena.empty.beforeLockout') }}
        </div>
        <va-data-table
          v-else
          :fields="standingsFields"
          :data="standings"
          :per-page="perPage"
          :row-class="rowClass"
          no-data-label="Nenhum bilhete"
        >
          <!--
            is_eliminated (misses > 0) = perdeu a faixa de 6, não é "fora".
            Com 1 erro o bilhete ainda concorre à faixa de 5.
          -->
          <template slot="situacao" slot-scope="props">
            <va-badge :color="badgeCor(props.rowData)">
              {{ $t(badgeRotulo(props.rowData)) }}
            </va-badge>
          </template>
        </va-data-table>
      </div>
    </va-card>
  </div>
</template>

<script>
import gql from 'graphql-tag'

const perPage = 25

const RANKING = gql`
  query SenaRanking($edition: Int!) {
    editionRow: sena_edition_by_pk(id: $edition) {
      id name edition_number status lockout_at
    }
    progress: sena_edition_progress(where: {sena_edition_id: {_eq: $edition}}) {
      games_total games_resolved games_blocked tickets_total
      tickets_alive tickets_alive_6 tickets_alive_5
    }
    ranking: sena_ranking(
      where: {sena_edition_id: {_eq: $edition}},
      order_by: {position: asc}
    ) {
      position
      sena_ticket_id
      client_id
      firstName
      hits
      misses
      is_winner
      since
    }
    standings: sena_standings(
      where: {sena_edition_id: {_eq: $edition}},
      order_by: [{is_eliminated: asc}, {hits: desc}, {created_at: asc}]
    ) {
      sena_ticket_id
      client_id
      firstName
      hits
      misses
      is_eliminated
      is_winner
      is_invalidated
      created_at
    }
  }
`

export default {
  name: 'SenaEditionRanking',
  props: {
    edition: { required: true },
  },
  data () {
    return {
      tab: 0,
      perPage,
      editionRow: null,
      progressRows: [],
      ranking: [],
      standings: [],
    }
  },
  apollo: {
    rankingData: {
      query: RANKING,
      variables () {
        return { edition: Number(this.edition) }
      },
      manual: true,
      // Ranking é ao vivo: acompanha o lançamento de placares feito em outra
      // aba. 30s é folgado — a mudança só acontece quando um admin salva.
      pollInterval: 30000,
      result ({ data, loading }) {
        if (loading || !data) return
        this.editionRow = data.editionRow
        this.progressRows = data.progress || []
        this.ranking = data.ranking || []
        this.standings = data.standings || []
      },
    },
  },
  computed: {
    progress () {
      return this.progressRows.length ? this.progressRows[0] : null
    },
    // A visibilidade no banco é gateada por lockout_at <= now(), NÃO por status.
    // Espelhar a mesma regra aqui evita mostrar "sem sobreviventes" quando na
    // verdade a view ainda não liberou nada.
    lockoutPassed () {
      const e = this.editionRow
      return !!(e && e.lockout_at && new Date(e.lockout_at) <= new Date())
    },
    cardTitle () {
      const e = this.editionRow
      return e ? `${this.$t('sena.cards.ranking')} — ${e.name}` : this.$t('sena.cards.ranking')
    },
    rankingFields () {
      const moment = this.$moment
      return [
        { name: 'position', title: this.$t('sena.headings.position'), width: '70px', dataClass: 'text-center' },
        { name: 'client_id', title: this.$t('sena.headings.clientId'), width: '100px', dataClass: 'text-center' },
        { name: 'firstName', title: this.$t('sena.headings.name') },
        { name: 'hits', title: this.$t('sena.headings.hits'), width: '90px', dataClass: 'text-center' },
        {
          name: 'since',
          title: this.$t('sena.headings.since'),
          width: '160px',
          callback (value) {
            return value ? moment(value).format('DD/MM/YYYY') : '—'
          },
        },
      ]
    },
    standingsFields () {
      const moment = this.$moment
      return [
        { name: 'client_id', title: this.$t('sena.headings.clientId'), width: '100px', dataClass: 'text-center' },
        { name: 'firstName', title: this.$t('sena.headings.name') },
        { name: 'hits', title: this.$t('sena.headings.hits'), width: '90px', dataClass: 'text-center' },
        { name: 'misses', title: this.$t('sena.headings.misses'), width: '90px', dataClass: 'text-center' },
        {
          name: '__slot:situacao',
          title: this.$t('sena.headings.situation'),
          width: '130px',
          dataClass: 'text-center',
          titleClass: 'text-center',
        },
        {
          name: 'created_at',
          title: this.$t('sena.headings.sentAt'),
          width: '170px',
          callback (value) {
            return moment(value).format('DD/MM/YYYY [às] HH:mm')
          },
        },
      ]
    },
  },
  methods: {
    badgeRotulo (row) {
      if (row.is_invalidated) { return 'sena.labels.invalidated' }
      if (row.misses >= 2) { return 'sena.labels.eliminated' }
      if (row.misses === 1) { return 'sena.labels.alive5' }
      return 'sena.labels.alive'
    },
    badgeCor (row) {
      if (row.is_invalidated) { return 'gray' }
      if (row.misses >= 2) { return 'danger' }
      if (row.misses === 1) { return 'warning' }
      return 'success'
    },
    rowClass (row) {
      return row.misses >= 2 ? 'sena-row--eliminated' : ''
    },
    refetchAll () {
      this.$apollo.queries.rankingData.refetch()
    },
  },
}
</script>

<style lang="scss">
.sena-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.sena-empty {
  padding: 2.5rem 1rem;
  text-align: center;
  color: #888888;
}

.sena-row--eliminated {
  opacity: 0.5;
}

.sena-muted {
  color: #888888;
}
</style>
